/**
 * FundraiserEmptyState.tsx - Empty State and Onboarding Interface
 *
 * This component displays an engaging empty state when no fundraisers exist,
 * providing onboarding guidance and marketing content to encourage fundraiser creation.
 *
 * Key Features:
 * - Marketing-focused onboarding content
 * - Feature highlights and benefits
 * - Call-to-action for creating first fundraiser
 * - Testimonials and success stories
 * - Interactive carousel with feature demonstrations
 * - Toggle between empty state and dashboard views
 *
 * Content Sections:
 * - Hero section with main value proposition
 * - Feature highlights with icons and descriptions
 * - Success metrics and social proof
 * - Getting started guidance
 *
 * Props:
 * - onCreateFundraiser: () => void - Handler for create fundraiser action
 * - showToggleLink?: boolean - Whether to show toggle back to dashboard
 * - onToggleView?: () => void - Handler for toggling back to dashboard
 *
 * Dependencies: Styled-components, Lucide icons
 */

import React, { useState } from "react";
import styled from "styled-components";
import { Plus, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { fundraiserStore } from "../stores";
import { AIFundraiserChat } from "./ai-fundraiser-create/AIFundraiserChat";
import { NavigateFunction } from "react-router-dom";
import { FlexContainer, withRouter } from "@mindmemobile/mm-shared-global";
import { observer } from "mobx-react-lite";
import {
  ApplicationSettingsStore,
  MerchantAccount,
} from "@mindmemobile/mm-payment-modules";
import AnalyticScreen from "../images/placeholder-screen/fundraiser-analytics.jpg";
import BuilderScreen from "../images/placeholder-screen/fundraiser-builder.jpg";
import DashboardScreen from "../images/placeholder-screen/fundraiser-dashboard.jpg";
import GalleryScreen from "../images/placeholder-screen/fundraiser-gallery.jpg";
import ShareScreen from "../images/placeholder-screen/share-fundraiser.jpg";

const EmptyStateContainer = styled.div`
  height: 100vh;
  overflow: auto;
  background: linear-gradient(
    135deg,
    #f8fafc 0%,
    #e2e8f0 25%,
    #f1f5f9 50%,
    #ffffff 100%
  );
  padding: 32px;
  display: flex;
  flex-direction: column;

  .apply-now-div {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;

    div,
    i {
      color: #7a5fc9;
      cursor: pointer;
      transition:
        transform 0.25s ease,
        text-shadow 0.25s ease;
    }

    div {
      font-size: 20px;
      font-weight: 600;
      line-height: 1.6;
      text-align: center;
      margin: 0 0 12px 0;
    }

    i {
      font-size: 26px;
      font-weight: 600;
      margin-left: 4px;
      margin-bottom: 10px;
    }

    &:hover {
      transform: scale(1.01);
      text-shadow: 0 2px 6px rgba(122, 95, 201, 0.18);
    }
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  &.header-with-btn {
    width: 1200px;
    max-width: 1700px;
    margin: 0 auto;
    gap: 35px;
    margin-bottom: 25px;
  }
  .back-btn-plh {
    margin-left: 20px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const HeaderTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
  text-align: center;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
  }
`;
const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  width: 100%;
  margin-bottom: 48px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 48px;
    margin-bottom: 32px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const MarketingDescription = styled.p`
  font-size: 20px;
  color: #64748b;
  line-height: 1.6;
  margin: 8px auto 24px;
  max-width: 800px;
  text-align: center;

  @media (max-width: 768px) {
    margin: 8px auto 16px;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: 20px; /* Moved bullets left by 30 pixels from previous position */
`;

const FeatureItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  &::before {
    content: "✔";
    /* background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M20 6L9 17l-5-5' stroke='black' stroke-width='3' fill='none'/%3E%3C/svg%3E"); */
    font-size: 24px;
    font-weight: 800;
    margin-top: 4px;
  }
`;

const FeatureDescription = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0;
  margin-left: 44px; /* Align with title text, accounting for checkmark icon and spacing */
  max-width: 420px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-left: -30px; /* Moved to the left by 30 pixels */
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  background: #f8fafc;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const PlaceholderImage = styled.div<{ bgColor: string }>`
  width: 100%;
  height: 100%;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const MediaIndicators = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 0;
`;

const Indicator = styled.button<{ isActive: boolean }>`
  width: ${(props) => (props.isActive ? "28px" : "12px")};
  height: 12px;
  border-radius: 6px;
  border: none;
  background: ${(props) => (props.isActive ? "#3b82f6" : "#cbd5e1")};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) => (props.isActive ? "#2563eb" : "#94a3b8")};
  }
`;

const CreateButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  /* width: 100%; */
`;

const CreateFundraiserButton = styled.button`
  background: linear-gradient(135deg, #10b981, #059669);
  color: #ffffff;
  border: none;
  padding: 20px 40px;
  border-radius: 16px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(16, 185, 129, 0.4);
  }

  &:active {
    transform: translateY(-2px);
  }
`;

const MerchantAccountCreationLink = styled.p`
  font-size: 20px;
  color: #7a5fc9;
  line-height: 1.6;
  margin: 0px 0px 12px 0px;
  text-align: center;
  font-weight: 600;
`;

const placeholderImages = [
  {
    id: 1,
    label: "Fundraiser Analytics",
    bgColor: "linear-gradient(135deg, #667eea, #764ba2)",
    image: AnalyticScreen,
  },
  {
    id: 2,
    label: "Donation Progress",
    bgColor: "linear-gradient(135deg, #f093fb, #f5576c)",
    image: DashboardScreen,
  },
  {
    id: 3,
    label: "Promoter Dashboard",
    bgColor: "linear-gradient(135deg, #4facfe, #00f2fe)",
    image: GalleryScreen,
  },
  {
    id: 4,
    label: "Fundraiser Builder",
    bgColor: "linear-gradient(135deg, #43e97b, #38f9d7)",
    image: BuilderScreen,
  },
  {
    id: 5,
    label: "Social Sharing",
    bgColor: "linear-gradient(135deg, #fa709a, #fee140)",
    image: ShareScreen,
  },
];

const features = [
  {
    title: "Launch Fundraisers Fast",
    description:
      "Set up and manage fundraisers effortlessly with our intuitive step-by-step builder.",
  },
  {
    title: "Give Donors Choice",
    description:
      "Support one-time or recurring gifts with optional fee coverage for maximum flexibility.",
  },
  {
    title: "Turn Supporters Into Fundraisers",
    description:
      "Empower promoters with tracking and outreach tools to boost donations.",
  },
  {
    title: "Design That Converts",
    description:
      "Customize layouts, colors, and media to create a visually stunning, on-brand fundraiser.",
  },
  {
    title: "Track Results Live",
    description:
      "Get real-time insights into donations and promoter performance with clear, actionable dashboards.",
  },
];
interface FundraiserEmptyStateProps {
  onCreateFundraiser: () => void;
  showToggleLink?: boolean;
  onToggleView?: () => void;
  navigate: NavigateFunction;
  merchantAccounts: MerchantAccount[];
  onAICreate?: () => void;
}

const FundraiserEmptyState: React.FC<FundraiserEmptyStateProps> = observer(
  ({
    onCreateFundraiser,
    showToggleLink = false,
    onToggleView,
    navigate,
    merchantAccounts,
    onAICreate,
  }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showAIChat, setShowAIChat] = React.useState(false);

    // Auto-rotate images every 5 seconds
    React.useEffect(() => {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) =>
          prev === placeholderImages.length - 1 ? 0 : prev + 1
        );
      }, 5000);

      return () => clearInterval(interval);
    }, []);

    const handleAIComplete = (fundraiserData: any) => {
      setShowAIChat(false);
      // Create new fundraiser from AI-generated data
      fundraiserStore.addFundraiserFromAI(fundraiserData);
      navigate("/fundraiser/builder");
    };

    const handleManualCreate = () => {
      setShowAIChat(false);
      // Create blank fundraiser for manual creation
      fundraiserStore.createBlankFundraiser();
      navigate("/fundraiser/builder");
    };

    const goToPrevious = () => {
      setCurrentImageIndex((prev) =>
        prev === 0 ? placeholderImages.length - 1 : prev - 1
      );
    };

    const goToNext = () => {
      setCurrentImageIndex((prev) =>
        prev === placeholderImages.length - 1 ? 0 : prev + 1
      );
    };

    const goToSlide = (index: number) => {
      setCurrentImageIndex(index);
    };

    const handleCreateNewFundraiser = () => {
      if (ApplicationSettingsStore.getOpenAISettings()) {
        setShowAIChat(true);
      } else {
        onCreateFundraiser();
      }
    };

    const applyNowClick = () => {
      if (window.parent) {
        const message = {
          type: "showMoneyTab",
        };
        window.parent.postMessage(JSON.stringify(message), "*");
      }
    };
    const currentImage = placeholderImages[currentImageIndex];

    return (
      <EmptyStateContainer>
        <Header
          className={`${fundraiserStore.hasActiveMerchantAccount() ? "header-with-btn" : ""}`}
        >
          <HeaderLeft>
            {showToggleLink && onToggleView && (
              <BackButton onClick={onToggleView} className="back-btn-plh">
                <ArrowLeft size={20} />
              </BackButton>
            )}
          </HeaderLeft>
          <HeaderTitle>
            Create Powerful Fundraisers That Drive Donations
          </HeaderTitle>
          {fundraiserStore.hasActiveMerchantAccount() && (
            <CreateButtonContainer>
              <CreateFundraiserButton
                disabled={!fundraiserStore.hasActiveMerchantAccount()}
                onClick={() => handleCreateNewFundraiser()}
              >
                <Plus size={24} />
                New Fundraiser
              </CreateFundraiserButton>
            </CreateButtonContainer>
          )}
        </Header>
        {!fundraiserStore.hasActiveMerchantAccount() && (
          <FlexContainer
            justifyContent="center"
            spacing={5}
            style={{
              width: "780px",
              margin: "0px auto 30px",
              justifyContent: "flex-start",
            }}
          >
            <MerchantAccountCreationLink>
              A merchant account is required to use Fundraisers.{" "}
            </MerchantAccountCreationLink>
            <div onClick={() => applyNowClick()} className="apply-now-div">
              <div>Apply Now</div>
              <i className="fa fa-solid fa-angle-right" aria-hidden="true"></i>
            </div>
          </FlexContainer>
        )}

        <MainContent>
          {/* <MarketingDescription>
            Launch impactful fundraisers with tools designed to maximize
            donations through community-driven promotion.
          </MarketingDescription>
          */}

          <ContentGrid>
            <LeftColumn>
              <FeaturesList>
                {features.map((feature, index) => (
                  <FeatureItem key={index}>
                    <FeatureTitle>{feature.title}</FeatureTitle>
                    <FeatureDescription>
                      {feature.description}
                    </FeatureDescription>
                  </FeatureItem>
                ))}
              </FeaturesList>
            </LeftColumn>

            <RightColumn>
              <ImageContainer>
                <PlaceholderImage bgColor={currentImage.bgColor}>
                  <img
                    src={currentImage.image}
                    width={"90%"}
                    height={"90%"}
                    alt="Placeholder"
                  />
                </PlaceholderImage>
              </ImageContainer>

              {placeholderImages.length > 1 && (
                <MediaIndicators>
                  {placeholderImages.map((_, index) => (
                    <Indicator
                      key={index}
                      isActive={index === currentImageIndex}
                      onClick={() => goToSlide(index)}
                      aria-label={"Go to slide " + (index + 1)}
                    />
                  ))}
                </MediaIndicators>
              )}
            </RightColumn>
          </ContentGrid>
        </MainContent>
        <AIFundraiserChat
          isVisible={showAIChat}
          onClose={() => setShowAIChat(false)}
          onComplete={handleAIComplete}
          onManualCreate={handleManualCreate}
          merchantAccounts={merchantAccounts}
          onAICreate={onAICreate}
        />
      </EmptyStateContainer>
    );
  }
);

export default withRouter(FundraiserEmptyState);
